import { cn, isV2Developer } from 'lib/utils';
import { InputMaybe, Marketplace } from 'app/graphql/generated/cart/graphql';
import { useQuery } from '@apollo/client';
import { PRODUCTS } from 'app/graphql';
import InfiniteScroll from 'components/ui/infinite-scroll';
import { Skeleton } from 'components/ui/skeleton';
import { createFallbackImageUrl } from 'pages/Catalog/utils';
import Logo from 'assets/icons/logo-icon-light.png';
import EmptyIcon from 'assets/images/no-products.svg';
import EmptyIconDark from 'assets/images/no-products-dark.svg';
import { EmptyState } from 'components/EmptyState';
import { useAppSelector } from 'app/store';
import { selectUserInfo, selectUserInfoLoading } from 'app/store/user';
import {
  Currency,
  ProductAvailabilityValue,
  ProductsSortInput,
} from 'app/graphql/generated/admin/graphql';
import { ClipboardCopy, Link2, Loader2, Webhook } from 'lucide-react';
import { useState } from 'react';
import * as Sentry from '@sentry/react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from 'components/ui/context-menu';
import { Typography } from 'components/ui/typography';
import { toast } from 'sonner';
import { useTriggerTestEvent } from 'pages/Catalog/hooks/useTriggerTestEvent';
import type { CatalogProduct } from 'pages/Catalog/components/ProductDetail';

type ListItemProps = {
  imageUrl?: string;
  productUrl: string;
  price: number;
  currency: Currency;
  title: string;
  storeName: string;
  storeLogo: string;
  storeCommissions: number;
  marketplace: Marketplace;
  className?: string;
  productId: string;
  isV2Developer: boolean;
  onSelect: () => void;
};

type ProductListProps = {
  storeDomains: string[];
  sortBy?: InputMaybe<ProductsSortInput>;
  minPrice?: number;
  maxPrice?: number;
  currencies: Currency[];
  onProductClick?: (product: CatalogProduct) => void;
};

const PRODUCTS_PER_PAGE = 20;

const initialProductsInputs = {
  filters: {
    availability: ProductAvailabilityValue.InStock,
  },
  pagination: {
    first: PRODUCTS_PER_PAGE,
  },
};

const ListItem = ({
  imageUrl,
  title,
  storeName,
  productUrl,
  storeCommissions,
  storeLogo,
  marketplace,
  className,
  price,
  currency,
  productId,
  isV2Developer,
  onSelect,
  ...props
}: ListItemProps) => {
  const [isError, setIsError] = useState(false);
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(price / 100);

  const fallbackBadge = storeName?.charAt(0)?.toLowerCase() ?? 'r';

  const triggerTestEvent = useTriggerTestEvent();

  return (
    <ContextMenu>
      <ContextMenuTrigger
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        className={cn(
          'group flex cursor-pointer flex-col border-b border-r border-[var(--line)] bg-[var(--surface)] transition-colors hover:bg-[var(--surface-2)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
          className,
        )}
        {...props}
      >
        <div className="relative aspect-square overflow-hidden bg-[#f3eedb]">
          {/* Brand badge — sharp 28×28 square pinned top-right with the
              store's first character (or logo). */}
          <div className="absolute right-2 top-2 z-10 grid h-7 w-7 place-items-center border border-[var(--line)] bg-[var(--rye-paper)] font-display text-[10px] font-medium lowercase text-[var(--rye-ink)]">
            {storeLogo ? (
              <img
                src={storeLogo}
                alt={`${storeName}'s logo`}
                className="h-full w-full object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <img
                src={createFallbackImageUrl(productUrl, marketplace)}
                alt=""
                className="h-full w-full object-contain"
                onError={(e) => {
                  const img = e.currentTarget as HTMLImageElement;
                  img.replaceWith(document.createTextNode(fallbackBadge));
                }}
              />
            )}
          </div>
          {imageUrl && !isError ? (
            <img
              src={imageUrl}
              alt={title}
              onError={() => setIsError(true)}
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <img src={Logo} alt="" className="h-12 w-12 opacity-30" />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 p-3 pb-3.5">
          <Typography
            variant="small"
            as="div"
            className="truncate font-semibold text-[var(--text)]"
          >
            {title}
          </Typography>
          <Typography variant="mono" as="div" className="truncate text-[11px]">
            Store · {storeName}
          </Typography>
          <Typography
            variant="mono"
            as="div"
            className="mt-1.5 flex items-center justify-between text-[12px]"
          >
            <span>
              commission · <span className="text-[var(--text)]">{storeCommissions}%</span>
            </span>
            <span className="font-semibold text-[var(--text)]">{formattedPrice}</span>
          </Typography>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-48">
        <ContextMenuItem
          className="flex items-center gap-2"
          onClick={() => {
            window.open(productUrl, '_blank');
          }}
        >
          <Link2 className="h-4 w-4" />
          View product
        </ContextMenuItem>
        <ContextMenuItem
          className="flex items-center gap-2"
          onClick={() => {
            navigator.clipboard.writeText(productId);
            toast.success(`Copied product ID to clipboard`);
          }}
        >
          <ClipboardCopy className="h-4 w-4" /> Copy product ID
        </ContextMenuItem>
        <ContextMenuItem
          className="flex items-center gap-2"
          onClick={() => {
            navigator.clipboard.writeText(productUrl);
            toast.success(`Copied product URL to clipboard`);
          }}
        >
          <ClipboardCopy className="h-4 w-4" /> Copy product URL
        </ContextMenuItem>
        {isV2Developer && (
          <ContextMenuItem
            className="flex items-center gap-2"
            disabled={triggerTestEvent.isPending}
            onClick={() =>
              triggerTestEvent.mutate(productUrl, {
                onSuccess: () => toast.success('Test event sent'),
                onError: (err) => {
                  console.error('Failed to trigger test event', err);
                  toast.error(err instanceof Error ? err.message : 'Failed to trigger test event');
                },
              })
            }
          >
            <Webhook className="h-4 w-4" /> Send test webhook
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

const LoadingSkeleton = () => (
  <div className="flex flex-col border-b border-r border-[var(--line)] bg-[var(--surface)]">
    <Skeleton className="aspect-square w-full" />
    <div className="flex flex-col gap-1.5 p-3">
      <Skeleton className="h-3.5 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
      <div className="mt-1 flex justify-between">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-12" />
      </div>
    </div>
  </div>
);

// catalog-grid — sharp, line-defined grid; auto-fill at 220px min so
// cards scale with the rail width without crowding the body text.
const Grid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] border-l border-t border-[var(--line)]">
    {children}
  </div>
);

export const ProductList = ({
  storeDomains,
  sortBy,
  minPrice,
  maxPrice,
  currencies,
  onProductClick,
}: ProductListProps) => {
  const { adminToken, billingPeriodSettings } = useAppSelector(selectUserInfo) || {};
  const loadingInfo = useAppSelector(selectUserInfoLoading);
  // v1 (legacy) developers can't handle the v2 webhook, so the trigger is v2-only.
  const isV2 = isV2Developer(billingPeriodSettings);

  const {
    fetchMore,
    loading: queryLoading,
    error,
    data,
  } = useQuery(PRODUCTS, {
    context: {
      clientName: 'admin',
    },
    skip: !adminToken || loadingInfo,
    variables: {
      ...initialProductsInputs,
      filters: {
        ...initialProductsInputs.filters,
        storeDomains,
        minPrice,
        maxPrice,
        currencies,
      },
      sort: sortBy,
    },
    onError: (error) => {
      Sentry.captureException(new Error('Failed to fetch products', { cause: error }));
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const products: any[] = data?.products.edges.map((edge: any) => edge.node) ?? [];
  const hasMore = data?.products.pageInfo.hasNextPage ?? false;
  const endCursor = data?.products.pageInfo.endCursor;

  const next = async () => {
    await fetchMore({
      variables: {
        pagination: {
          after: endCursor,
          first: PRODUCTS_PER_PAGE,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        return {
          products: {
            ...fetchMoreResult.products,
            edges: [...prev.products.edges, ...fetchMoreResult.products.edges],
            pageInfo: fetchMoreResult.products.pageInfo,
          },
        };
      },
    });
  };

  if (error) {
    return (
      <EmptyState
        iconLight={EmptyIcon}
        iconDark={EmptyIconDark}
        imageClassName="h-24 w-24"
        title="Failed to load products"
        description={error.message}
        className="my-0 py-12"
      />
    );
  }

  if (queryLoading) {
    return (
      <div>
        <Grid>
          {Array.from({ length: 12 }).map((_, i) => (
            <LoadingSkeleton key={i} />
          ))}
        </Grid>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <EmptyState
        iconLight={EmptyIcon}
        iconDark={EmptyIconDark}
        imageClassName="h-48 w-48"
        title="No products for selected filters"
        className="my-0 py-12"
      />
    );
  }

  return (
    <div>
      <Grid>
        {products.map((product, index) => {
          const isShopify = product.__typename === 'ShopifyProduct';
          const marketplace = isShopify ? Marketplace.Shopify : Marketplace.Amazon;
          const storeName = isShopify ? product.store : 'Amazon';
          const storeLogo = isShopify ? product.storeLogo ?? '' : '';
          const storeCommissions = isShopify ? product.storeCommissions ?? 0 : 0;
          const vendor = isShopify ? product.vendor ?? null : null;
          return (
            <ListItem
              productId={product.id}
              isV2Developer={isV2}
              key={product.id + index}
              imageUrl={product.images?.[0]?.url}
              title={product.title}
              productUrl={product.url}
              price={product.price}
              currency={product.currency}
              storeName={storeName}
              storeLogo={storeLogo}
              storeCommissions={storeCommissions}
              marketplace={marketplace}
              onSelect={() =>
                onProductClick?.({
                  id: product.id,
                  title: product.title,
                  url: product.url,
                  images: product.images,
                  price: product.price,
                  currency: product.currency,
                  marketplace,
                  vendor,
                  storeName,
                  storeLogo,
                  storeCommissions,
                })
              }
            />
          );
        })}
      </Grid>
      <InfiniteScroll hasMore={hasMore} isLoading={queryLoading} next={next} threshold={1}>
        {hasMore && (
          <div className="mt-6 flex w-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[var(--text-muted)]" />
          </div>
        )}
      </InfiniteScroll>
    </div>
  );
};
